/**
 * Search types — canonical API contract.
 *
 * The backend conforms to these types. Mirrors
 * `go/internal/search/models.go`.
 *
 * The single user endpoint is POST /v1/search (catalog + file content via the
 * scope arg). The recent view is just a search with empty query + recency
 * sort. Admin cross-tenant search, reindex, and chunk ingest are the
 * other paths on the search resource.
 */

export type SearchMode = 'hybrid' | 'semantic' | 'lexical';
export type SearchSort = 'relevance' | 'updated_at_desc' | 'updated_at_asc';

export const ALL_ENTITY_TYPES = [
  'workspace',
  'team',
  'project',
  'initiative',
  'ticket',
  'persona',
  'automation',
  'integration',
  'tool_endpoint',
  'session',
  'agent_process',
  'session_message',
  'session_event',
  'file',
  'directory',
  'feed_post',
  'account_skill',
  'account_artifact',
  'account_secret',
] as const;

export type EntityType = (typeof ALL_ENTITY_TYPES)[number];

export interface SearchRequest {
  query: string;
  mode?: SearchMode;
  sort?: SearchSort;
  scope?: SearchScope;
  entity_types?: EntityType[];
  /** Restrict to a set of workspaces (a list). Absent = default scope. */
  workspace_id?: string[];
  limit?: number;
  offset?: number;
}

/** Search corpus scope. */
type SearchScope = 'all' | 'catalog' | 'files';

export interface SearchResult {
  entity_type: EntityType;
  entity_id: string;
  title: string;
  snippet: string;
  parent_context?: string;
  route: string;
  route_params: Record<string, string>;
  updated_at: string;
  score: number;

  message_role?: string;
  started_at?: string;
  last_message_at?: string;

  session_id?: string;
  workspace_id?: string;
  task_id?: string;
  message_index?: number;

  // --- File-chunk results (entity_type "file") ---
  file_path?: string;
  start_line?: number;
  end_line?: number;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
}
